/**
 * <casa-pyxd-canvas>
 *
 * PDP fabric customization, built to the "(d)pyxmagic-modal popup" Figma
 * frame (node 11586:10493). Wraps the Pyxd Draping SDK with Casa's own
 * customization panel and Fabrics drawer.
 *
 * Design notes honoured (from the frame's Notes layers):
 *   - Featured fabrics show at the top, a different count on desktop vs
 *     mobile (up to ~10). See _renderFeatured.
 *   - Filters auto-apply on check (no Apply button) and the menu minimises
 *     when you click outside the filter area. See _onDocumentClick.
 *   - Selecting a swatch drapes the model immediately. See _selectSwatch.
 *
 * Pyxd only exposes a `body` surface for these frames, so Legs and Castors
 * are captured as selections but cannot re-render the 3D model.
 */

/** Facet vocabularies. Pyxd swatches carry only a label, so we derive
 *  filter facets from it — there is no structured metadata on the swatch. */
const COLOR_FACETS = [
  { label: 'White', swatch: '#ffffff', match: /white|blanc|ivory|natural|latte|parchment/i },
  { label: 'Brown', swatch: '#6b4423', match: /brown|mushroom|biscotti|walnut|nero|bruno/i },
  { label: 'Green', swatch: '#4a5d3a', match: /green|forest|foglia|sage|olive/i },
  { label: 'Coral', swatch: '#d4552f', match: /coral|terracotta|rust|clay/i },
  { label: 'Tan', swatch: '#d9cbb3', match: /tan|natural|sand|raffia|beige|quadri/i },
  { label: 'Yellow', swatch: '#e8c33f', match: /yellow|gold|ochre|mustard/i },
  { label: 'Red', swatch: '#a5322b', match: /red|crimson|cherry/i },
  { label: 'Teal', swatch: '#1f9e8f', match: /teal|aqua|turquoise/i },
  { label: 'Blue', swatch: '#4a6d9e', match: /blue|sky|azure|french|venezia/i },
];

const DESIGN_FACETS = [
  { label: 'Solid', match: /linen|velvet|mohair|quadri|raffia/i },
  { label: 'Stripe', match: /stripe|ticking|quadri/i },
  { label: 'Floral', match: /floral|palampore|animal kingdom|giardino|fleur/i },
  { label: 'Geometric', match: /bambu|geometric|trellis|lattice/i },
];

const FABRIC_FACETS = [
  { label: 'Linen', match: /linen/i },
  { label: 'Velvet', match: /velvet/i },
  { label: 'Mohair', match: /mohair/i },
  { label: 'Cotton', match: /cotton|ticking/i },
  { label: 'Raffia', match: /raffia/i },
];

const FACET_GROUPS = { color: COLOR_FACETS, design: DESIGN_FACETS, fabric: FABRIC_FACETS };

/** "Plains" vs "Patterns" grouping in the drawer results. */
const PATTERN_RE = /stripe|ticking|floral|palampore|animal kingdom|giardino|bambu|damasco/i;

class CasaPyxdCanvas extends HTMLElement {
  constructor() {
    super();
    this._onSwatchClick = this._onSwatchClick.bind(this);
    this._onDocumentClick = this._onDocumentClick.bind(this);
    this._onKeydown = this._onKeydown.bind(this);
    this._onDrawerClick = this._onDrawerClick.bind(this);
    this._onBreakpointChange = this._onBreakpointChange.bind(this);
    // Option clicks are ignored for frame switching until the base frame is
    // fully registered, and each click gets a sequence number so a slow
    // lookup cannot overtake a later click.
    this._ready = false;
    this._optionSeq = 0;
    this._pendingOptionChange = false;
    this._switching = false;
  }

  /** Desktop and mobile have separate gallery DOM trees, so the viewport has
   *  to be re-mounted when the breakpoint changes. Without this, resizing or
   *  rotating across 749px strands the canvas in the now-hidden tree and the
   *  visualiser disappears. */
  _onBreakpointChange() {
    // While the drawer is open the viewport lives in the drawer preview;
    // re-mounting now would yank it out mid-use. _restoreViewport() already
    // re-mounts on close, so defer to that.
    if (this.drawer && this.drawer.open) {
      this._remountPending = true;
      return;
    }
    this._mountViewportInGallery();
  }

  async connectedCallback() {
    this.companyId = this.dataset.companyId || '';
    this.productId = this.dataset.productId || '';
    // Resolved at runtime from Pyxd, never from markup.
    this.frameId = '';
    this.productHandle = this.dataset.productHandle;
    this.productTitle = this.dataset.productTitle || '';
    this.basePrice = parseInt(this.dataset.productPrice, 10) || 0;
    this.variantId = this.dataset.variantId;
    this.variants = this._parseJson(this.dataset.variants) || [];
    this.optionNames = this._parseJson(this.dataset.optionNames) || [];
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.featuredDesktop = parseInt(this.dataset.featuredDesktop, 10) || 6;
    this.featuredMobile = parseInt(this.dataset.featuredMobile, 10) || 4;
    this.defaultYardage = parseFloat(this.dataset.yardage) || 10;
    this.yardage = this._variantYardage(
      this.variants.find((v) => String(v.id) === String(this.variantId))
    );
    this.swatchMap = this._parseSwatchMap(this.dataset.swatchMap);
    this.fabricCache = new Map();
    this.storageKey = `pyxd:selection:${this.productHandle}`;

    this.canvas = this.querySelector('canvas');
    this.statusEl = this.querySelector('[data-pyxd-status]');
    this.featuredEl = this.querySelector('[data-pyxd-featured]');
    this.errorEl = this.querySelector('[data-pyxd-error]');
    this.fabricNameEl = this.querySelector('[data-pyxd-fabric-name]');
    this.comToggle = this.querySelector('[data-pyxd-com-toggle]');
    this.comNote = this.querySelector('[data-pyxd-com-note]');
    this.additionalEl = this.querySelector('[data-pyxd-additional]');
    this.yardageEl = this.querySelector('[data-pyxd-yardage]');
    this.drawer = this.querySelector('[data-pyxd-drawer]');
    this.resultsEl = this.querySelector('[data-pyxd-results]');
    this.countEl = this.querySelector('[data-pyxd-count]');
    this.appliedEl = this.querySelector('[data-pyxd-applied]');
    this.mCountEl = this.querySelector('[data-pyxd-mcount]');
    this.mGroupsEl = this.querySelector('[data-pyxd-mgroups]');
    this.mAppliedEl = this.querySelector('[data-pyxd-mapplied]');
    this.previewSlot = this.querySelector('[data-pyxd-drawer-preview]');

    if (!this.canvas || !this.productId) {
      this._dismiss();
      return;
    }

    this.filters = { color: new Set(), design: new Set(), fabric: new Set() };
    this.comSelected = false;

    this._bindPanel();
    this._bindDrawer();
    this._bindOptions();

    let pyxd;
    try {
      pyxd = await this._waitForPyxd();
    } catch (err) {
      // No SDK, no visualiser: leave the page's own buy form in charge.
      console.error('[casa-pyxd-canvas]', err);
      this._dismiss();
      return;
    }
    this.pyxd = pyxd;

    // Whether this product is drapable is Pyxd's call: they key their frames
    // on our Shopify product IDs, so one lookup answers it and the theme
    // keeps no list. Nothing found means this block was never meant for the
    // page, so it removes itself and the native variant picker stays.
    // By product ID first; then by product title, because Pyxd names frames
    // after the piece ("Brighton Slipper Chair") and resolves those names,
    // which covers a frame they rendered but never linked to our ID.
    this.baseFrame = await this._lookupFrame(this.productId);
    if (!this.baseFrame) {
      for (const title of this._titleKeys()) {
        this.baseFrame = await this._lookupFrame(title);
        if (this.baseFrame) {
          // Filed under the product ID too, which is the key the section's
          // pre-paint script reads, so repeat views skip the buy-form flash.
          this._storeFrame(this.productId, this.baseFrame, true);
          break;
        }
      }
    }
    if (!this.baseFrame) {
      this._dismiss();
      return;
    }
    const frame = (await this._frameForVariant(this._currentVariant())) || this.baseFrame;
    this.frameId = frame.frameId;

    this._reveal();
    this._mountViewportInGallery();

    this._mobileQuery = window.matchMedia('(max-width: 749px)');
    if (this._mobileQuery.addEventListener) {
      this._mobileQuery.addEventListener('change', this._onBreakpointChange);
    } else if (this._mobileQuery.addListener) {
      // Safari < 14
      this._mobileQuery.addListener(this._onBreakpointChange);
    }

    const saved = this._readSaved();
    // A `fabric` param in the URL wins over the saved selection, so a shared
    // link always opens on the configuration the sender was looking at.
    const urlFabric = this._swatchFromUrl();
    let initialConfig = saved && saved.configuration ? saved.configuration : undefined;
    if (urlFabric && urlFabric !== 'com') {
      initialConfig = { frameId: this.frameId, surfaces: { body: urlFabric } };
    }
    // A saved configuration may belong to another size/back of this product;
    // the frame decided above always wins, only the fabric carries over.
    if (initialConfig) initialConfig = { ...initialConfig, frameId: this.frameId };

    this._setStatus('Loading…');
    // Pyxd reads clientWidth/Height at register time and won't auto-resize.
    await this._waitForCanvasSize();
    try {
      await pyxd.registerCanvas(this.canvas, this.frameId, initialConfig);
    } catch (err) {
      console.error('[casa-pyxd-canvas] registerCanvas rejected', err);
      this._setStatus('Could not load 3D model.');
      return;
    }
    this._setStatus('');

    // registerCanvas resolves before Pyxd's surface/swatch state is populated.
    const ready = await this._waitForReady(pyxd);
    if (!ready) {
      console.error('[casa-pyxd-canvas] surfaces/swatches not available after wait');
      if (this.featuredEl) {
        this.featuredEl.innerHTML =
          '<p class="casa-pyxd-drawer__empty">Could not load fabric options. Please refresh.</p>';
      }
      return;
    }

    this.surfaceId = ready.surfaceId;
    this.swatches = this._decorateSwatches(ready.swatches);

    // Remember the frame's own starting fabric. COM means the customer
    // supplies their own material, so selecting it must return the render
    // to this default rather than leaving the last-picked fabric draped.
    if (!saved || !saved.configuration) {
      this._defaultSwatchId = this._currentSwatchId();
    } else {
      // A saved selection already replaced the default before registerCanvas,
      // so fall back to the first swatch Pyxd lists for the surface.
      this._defaultSwatchId =
        (this.swatches[0] && this.swatches[0].swatchId) || this._currentSwatchId();
    }

    if (urlFabric === 'com') this._setCom(true);
    else if (!urlFabric && saved && saved.com) this._setCom(true);

    this._renderFeatured();
    this._renderFilterMenus();
    this._renderResults();
    this._syncSelectionLabels();
    this._syncOptionAvailability();
    this._refreshPricing();

    this._ready = true;
    if (this._pendingOptionChange) {
      this._pendingOptionChange = false;
      this._onOptionChange();
    }
  }

  _decorateSwatches(list) {
    return (list || []).map((s) => ({
      ...s,
      facets: {
        color: COLOR_FACETS.filter((f) => f.match.test(s.label)).map((f) => f.label),
        design: DESIGN_FACETS.filter((f) => f.match.test(s.label)).map((f) => f.label),
        fabric: FABRIC_FACETS.filter((f) => f.match.test(s.label)).map((f) => f.label),
      },
      group: PATTERN_RE.test(s.label) ? 'Patterns' : 'Plains',
    }));
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onDocumentClick);
    document.removeEventListener('keydown', this._onKeydown);
    if (this.drawer) this.drawer.removeEventListener('click', this._onDrawerClick);
    if (this._mobileQuery) {
      if (this._mobileQuery.removeEventListener) {
        this._mobileQuery.removeEventListener('change', this._onBreakpointChange);
      } else if (this._mobileQuery.removeListener) {
        this._mobileQuery.removeListener(this._onBreakpointChange);
      }
    }
    if (this.pyxd) {
      try { this.pyxd.releaseCanvas(); } catch (e) { /* ignore */ }
    }
  }

  /* ---------- Runtime frame resolution ---------- */

  _parseJson(raw) {
    if (!raw) return null;
    try { return JSON.parse(raw); } catch (e) { return null; }
  }

  /** No frame for this product (or no SDK): take the block out of the page so
   *  the theme's own variant picker and buy buttons stay in charge. */
  _dismiss() {
    // A repeat view may have pre-set the class from the cache; undo it so
    // the native controls come back.
    const section = this.closest('.shopify-section');
    if (section) section.classList.remove('pyxd-active');
    this.remove();
  }

  _reveal() {
    this.hidden = false;
    const section = this.closest('.shopify-section');
    if (section) section.classList.add('pyxd-active');
    // The details column just grew by the whole panel; anything that sized
    // itself from it (the sticky offset) needs to re-measure.
    window.dispatchEvent(new Event('resize'));
  }

  _bindOptions() {
    this.querySelectorAll('[data-pyxd-option-value]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const group = btn.closest('[data-pyxd-option-group]');
        if (!group) return;
        group.querySelectorAll('[data-pyxd-option-value]').forEach((b) => {
          b.setAttribute('aria-checked', b === btn ? 'true' : 'false');
        });
        const label = group.querySelector('[data-pyxd-option-selected]');
        if (label) label.textContent = btn.dataset.pyxdOptionValue;
        this._onOptionChange();
      });
    });
  }

  /** The Shopify variant matching the chosen option buttons. Options with a
   *  single value are not rendered, so only rendered ones are matched; the
   *  variant from markup is the fallback. */
  _currentVariant() {
    if (!this.variants.length) return null;
    const wanted = Array.from(this.querySelectorAll('[data-pyxd-option-group]'))
      .map((group) => group.querySelector('[data-pyxd-option-value][aria-checked="true"]'))
      .filter(Boolean)
      .map((btn) => ({ name: btn.dataset.pyxdOptionName, value: btn.dataset.pyxdOptionValue }));
    if (wanted.length) {
      const match = this.variants.find((v) =>
        wanted.every(({ name, value }) => {
          const index = this.optionNames.indexOf(name);
          return index === -1 || (v.options || [])[index] === value;
        })
      );
      if (match) return match;
    }
    return (
      this.variants.find((v) => String(v.id) === String(this.variantId)) || this.variants[0] || null
    );
  }

  _variantYardage(variant) {
    return (variant && parseFloat(variant.yardage)) || this.defaultYardage;
  }

  async _onOptionChange() {
    const variant = this._currentVariant();
    if (!variant) return;
    const seq = ++this._optionSeq;
    this.variantId = String(variant.id);
    this.basePrice = variant.price;
    this.yardage = this._variantYardage(variant);
    this._syncVariantUrl(variant);
    this._syncHeaderPrice();
    this._refreshPricing();
    if (!this.pyxd || !this.baseFrame) return;
    if (!this._ready) {
      // The base frame is still registering; connectedCallback replays the
      // last click once it is done.
      this._pendingOptionChange = true;
      return;
    }
    const frame = (await this._frameForVariant(variant)) || this.baseFrame;
    if (seq !== this._optionSeq) return; // a later click owns the outcome
    await this._switchFrame(frame);
  }

  /** Keep the address bar on the chosen variant so reload and share land on it. */
  _syncVariantUrl(variant) {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      window.history.replaceState(window.history.state, '', url.toString());
    } catch (e) { /* ignore */ }
  }

  /** The section's own price header is rendered server-side for the initial
   *  variant (with trade pricing applied in Liquid). Mirror the chosen
   *  variant's price there, using the same multiplier the header used. */
  _syncHeaderPrice() {
    const section = this.closest('.shopify-section');
    const priceEl = section && section.querySelector('.product-information__price');
    if (!priceEl) return;
    const multiplier = parseInt(this.dataset.priceMultiplier, 10) || 100;
    const cents = Math.round((this.basePrice * multiplier) / 100);
    // Same market-aware formatter as the panel (CASA-462); the header drops
    // a zero decimal part, as its Liquid does with `remove: '.00'`.
    let formatted = this._formatMoney(cents);
    const pattern = this._moneyPattern();
    if (pattern.decimal) formatted = formatted.replace(pattern.decimal + '00', '');
    const textNode = Array.from(priceEl.childNodes).find(
      (n) => n.nodeType === Node.TEXT_NODE && /\S/.test(n.textContent)
    );
    if (textNode) textNode.textContent = ` ${formatted} `;
  }

  /** Grey out option values whose every variant is sold out, using the
   *  variant data already on the element. */
  _syncOptionAvailability() {
    this.querySelectorAll('[data-pyxd-option-value]').forEach((btn) => {
      const index = this.optionNames.indexOf(btn.dataset.pyxdOptionName);
      if (index === -1) return;
      const value = btn.dataset.pyxdOptionValue;
      const anyAvailable = this.variants.some((v) => (v.options || [])[index] === value && v.available !== false);
      btn.disabled = !anyAvailable;
      btn.setAttribute('aria-disabled', anyAvailable ? 'false' : 'true');
    });
  }

  /** One lookup against Pyxd by any flexible ID: a Shopify product or variant
   *  ID they registered, a frame ID, or a frame label. Resolves
   *  { frameId, label } or null when Pyxd has nothing for it. Cached for the
   *  session so toggling options costs no requests. */
  async _lookupFrame(key) {
    key = String(key || '').trim();
    if (!key || !this.pyxd) return null;
    this._frameCache = this._frameCache || new Map();
    if (this._frameCache.has(key)) return this._frameCache.get(key);

    // localStorage rather than sessionStorage: the section reads a hit
    // before first paint on later visits to skip the native-controls flash.
    const storageKey = `pyxd:frame:${this.companyId}:${key}`;
    try {
      const cached = JSON.parse(localStorage.getItem(storageKey) || 'null');
      if (cached && cached.until > Date.now()) {
        this._frameCache.set(key, cached.frame);
        return cached.frame;
      }
    } catch (e) { /* no storage */ }

    let frame = null;
    let notFound = false;
    try {
      if (typeof this.pyxd.loadFrameConfig === 'function') {
        // Same call lookup() makes, but it also returns the frame label,
        // which sibling-frame derivation needs. lookup() runs init() first
        // (reads the company ID off the script tag); loadFrameConfig() does
        // not, so do it here or it throws "No company ID provided."
        if (typeof this.pyxd.init === 'function') this.pyxd.init();
        const config = await this.pyxd.loadFrameConfig(key);
        if (config && config.frameId) frame = { frameId: config.frameId, label: config.frameLabel || '' };
      } else {
        const frameId = await this.pyxd.lookup(key);
        if (frameId) frame = { frameId, label: '' };
      }
    } catch (err) {
      const message = err && err.message ? err.message : '';
      notFound = /not found/i.test(message);
      if (!notFound) console.warn('[casa-pyxd-canvas] lookup failed for', key, err);
    }

    // Only definitive answers are kept across views: a hit for a day, a
    // "frame not found" for an hour (so the candidate PDPs without a frame do
    // not re-ask constantly, while a newly mapped product shows up same day).
    // A network error or a Pyxd 500 is never cached, or one bad response
    // would hide the visualiser for an hour.
    this._storeFrame(key, frame, frame || notFound);
    return frame;
  }

  _storeFrame(key, frame, definitive) {
    this._frameCache.set(String(key), frame);
    if (!definitive) return;
    try {
      const ttl = frame ? 24 * 60 * 60 * 1000 : 60 * 60 * 1000;
      localStorage.setItem(
        `pyxd:frame:${this.companyId}:${key}`,
        JSON.stringify({ frame, until: Date.now() + ttl })
      );
    } catch (e) { /* ignore */ }
  }

  /** Title keys for the base-frame lookup: the title as is, then without a
   *  made-to-order prefix. Casa titles the configurable piece "Custom Pillow"
   *  where Pyxd's frame is just "Pillow". */
  _titleKeys() {
    const title = (this.productTitle || '').trim();
    if (!title) return [];
    const bare = title.replace(/^(?:custom|com|made[\s-]to[\s-]order)\b[\s:-]*/i, '').trim();
    return bare && bare !== title ? [title, bare] : [title];
  }

  /** Frame for one variant. In order: the variant's own Shopify ID (should
   *  Pyxd register those), frame labels derived from the product's base frame
   *  label and this variant's option values, then the product's base frame. */
  async _frameForVariant(variant) {
    if (!variant || this.variants.length < 2 || !this.baseFrame) return this.baseFrame;
    const derived = this._derivedLabels(variant);
    const candidates = [String(variant.id)].concat(derived);
    // All candidates at once: the variant-ID alias is a guaranteed 404 today,
    // and the reveal should not wait on it before trying the labels.
    const results = await Promise.all(candidates.map((candidate) => this._lookupFrame(candidate)));
    const hit = results.find(Boolean);
    if (hit) return hit;
    if (derived.length) {
      console.warn(
        '[casa-pyxd-canvas] no Pyxd frame for variant',
        variant.title,
        '- showing',
        this.baseFrame.label || this.baseFrame.frameId
      );
    }
    return this.baseFrame;
  }

  /** Pyxd names sibling frames after the option that separates them:
   *  "Brighton Camel Back Sofa" / "Brighton Straight Back Sofa",
   *  "Brighton Octagonal Ottoman 30" / "... 48". From the base frame's label
   *  and this variant's option values, build the labels those siblings would
   *  carry:
   *   - a sibling value found in the base label is swapped for this value
   *     ("Camel Back" -> "Straight Back");
   *   - a trailing number in the label is swapped for the number in which this
   *     value differs from its siblings, position by position
   *     (48" x 30" vs 48" x 48": second number, 30 -> 48).
   *  A label that is not a real frame simply fails the lookup. */
  _derivedLabels(variant) {
    const base = (this.baseFrame && this.baseFrame.label) || '';
    if (!base) return [];
    const out = [];
    const push = (label) => {
      if (label && label !== base && !out.includes(label)) out.push(label);
    };
    const lower = base.toLowerCase();
    const numbers = (text) => String(text).match(/\d+(?:\.\d+)?/g) || [];
    const trailing = base.match(/(\d+(?:\.\d+)?)\s*$/);

    (variant.options || []).forEach((value, index) => {
      const siblings = Array.from(new Set(this.variants.map((v) => (v.options || [])[index]).filter(Boolean)));
      if (siblings.length < 2) return;

      siblings.forEach((other) => {
        if (other === value) return;
        const at = lower.indexOf(String(other).toLowerCase());
        if (at !== -1) push(base.slice(0, at) + value + base.slice(at + String(other).length));
      });

      if (!trailing) return;
      const mine = numbers(value);
      if (!mine.length) return;
      const columns = siblings.map(numbers).filter((n) => n.length);
      const width = Math.max(...columns.map((n) => n.length));
      for (let position = 0; position < width; position++) {
        const column = columns.filter((n) => n.length > position).map((n) => n[position]);
        if (new Set(column).size > 1 && mine[position] !== undefined) {
          push(base.slice(0, trailing.index) + mine[position]);
        }
      }
    });
    return out;
  }

  /** Re-register the canvas on another frame, carrying the chosen fabric over.
   *
   *  registerCanvas() only kicks off the SDK's preload and resolves at once,
   *  so "registered" is not "loaded": the frame is confirmed by polling the
   *  SDK config, and only then are the swatch list, labels, pricing and the
   *  saved selection rebuilt (sibling frames carry different swatch sets). */
  async _switchFrame(frame) {
    if (!frame || !this.pyxd || !this.canvas) return;
    this._targetFrameId = frame.frameId;
    // Already showing this frame and nothing else in flight: nothing to do.
    // If a switch IS in flight the SDK may still land on the other frame, so
    // re-register the wanted one.
    if (frame.frameId === this.frameId && !this._switching) return;
    this._switching = true;

    const swatchId = this._currentSwatchId();
    const surfaces = {};
    if (swatchId && this.surfaceId) surfaces[this.surfaceId] = swatchId;

    this._setStatus('Loading…');
    try {
      try { this.pyxd.releaseCanvas(); } catch (e) { /* nothing registered */ }
      await this.pyxd.registerCanvas(this.canvas, frame.frameId, { frameId: frame.frameId, surfaces });
    } catch (err) {
      console.error('[casa-pyxd-canvas] registerCanvas rejected', err);
      this._setStatus('Could not load 3D model.');
      this._switching = false;
      return;
    }

    const loaded = await this._waitForFrame(frame.frameId);
    if (this._targetFrameId !== frame.frameId) return; // superseded by a later click
    if (!loaded) {
      console.error('[casa-pyxd-canvas] frame did not load', frame.frameId);
      this._setStatus('Could not load 3D model.');
      this._switching = false;
      return;
    }
    this.frameId = frame.frameId;

    const ready = await this._waitForReady(this.pyxd);
    if (this._targetFrameId !== frame.frameId) return;
    this._switching = false;
    this._setStatus('');
    if (ready) {
      this.surfaceId = ready.surfaceId;
      this.swatches = this._decorateSwatches(ready.swatches);
      const stillThere = this.swatches.some((s) => s.swatchId === this._defaultSwatchId);
      if (!stillThere) this._defaultSwatchId = (this.swatches[0] || {}).swatchId;
      this._renderFeatured();
      this._renderFilterMenus();
      this._renderResults();
      this._syncPressedState(this._currentSwatchId());
    }
    this._syncSelectionLabels();
    this._refreshPricing();
    this._autoSave();
  }

  /** Resolves true once the SDK's own state reports the wanted frame. */
  async _waitForFrame(frameId, timeoutMs = 15000) {
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      let current = null;
      try { current = (this.pyxd.getConfig() || {}).frameId; } catch (e) { /* not loaded yet */ }
      if (current === frameId) return true;
      await new Promise((r) => setTimeout(r, 150));
    }
    return false;
  }

  /* ---------- Gallery mount ---------- */

  /** Pyxd renders a single image, and the agreed behaviour is that it takes
   *  over the product's MAIN image rather than sitting in its own box. We
   *  move the viewport into the first slot of the existing gallery (both the
   *  desktop stack and the mobile carousel) and hide the original first
   *  image. The remaining gallery images are Casa's own photography and stay
   *  as they are — they will not reflect the chosen fabric.
   *
   *  If the gallery isn't found we leave the viewport inline, so the
   *  visualiser always renders somewhere. */
  /** Undo the previous mount: put back the image it replaced and remove the
   *  wrapper it created, so switching breakpoints leaves no stale slot or
   *  duplicated image behind in the other gallery tree. */
  _reattachDetached() {
    if (this._detached && this._detached.length) {
      this._detached.forEach(({ el, placeholder }) => {
        if (placeholder && placeholder.parentElement) {
          placeholder.parentElement.replaceChild(el, placeholder);
        }
      });
      this._detached = [];
    }

    // The desktop mount wraps the viewport in its own slot element; drop it
    // once the viewport has moved on, otherwise it lingers as an empty node
    // and the desktop image count drifts.
    const slot = this._previousSlot || this._gallerySlot;
    this._previousSlot = null;
    if (slot && slot.classList && slot.classList.contains('casa-pyxd__gallery-slot')) {
      if (!slot.querySelector('.casa-pyxd__viewport-col') && slot.parentElement) {
        slot.parentElement.removeChild(slot);
      }
    }

    // The mobile mount reuses a real carousel slide, so only strip the marker.
    if (slot && slot.classList && slot.classList.contains('casa-pyxd__gallery-slide')) {
      if (!slot.querySelector('.casa-pyxd__viewport-col')) {
        slot.classList.remove('casa-pyxd__gallery-slide');
      }
    }
  }

  _mountViewportInGallery() {
    const viewport =
      this.viewportEl || this.querySelector('.casa-pyxd__viewport-col');
    const gallery = document.querySelector('casa-product-gallery');
    if (!viewport || !gallery) return;

    // Keep a handle: the viewport ends up outside this component.
    this.viewportEl = viewport;

    // Remember the slot from the previous mount so it can be cleaned up.
    this._previousSlot = this._gallerySlot || null;

    const desktop = gallery.querySelector('.casa-product-gallery__desktop');
    const mobileTrack = gallery.querySelector(
      '.casa-product-gallery__main-carousel .splide__list'
    );

    // Desktop stack and mobile carousel are separate DOM trees and only one
    // is displayed at a time. Use the media query rather than offsetParent,
    // which is unreliable before the carousel has initialised.
    // Detach rather than set [hidden]: the theme styles slide/gallery images
    // with an explicit `display`, which beats the UA's `[hidden]{display:none}`
    // and leaves the original photo painted behind the canvas. Keep a
    // reference so it can be put back when the breakpoint changes.
    const detach = (el) => {
      if (!el || !el.parentElement) return;
      const placeholder = document.createComment('casa-pyxd-replaced');
      el.parentElement.replaceChild(placeholder, el);
      this._detached = this._detached || [];
      this._detached.push({ el, placeholder });
    };

    if (this._isMobile() && mobileTrack) {
      // On mobile the render must BE the first slide, not sit above the
      // carousel — otherwise it reads as a separate block and the page
      // scrolls behind it.
      const firstSlide = mobileTrack.querySelector('.splide__slide');
      if (!firstSlide) return;
      if (viewport.parentElement === firstSlide) return;

      // Move the viewport first, THEN undo the previous mount — otherwise the
      // old slot still contains it and cannot be cleaned up.
      firstSlide.classList.add('casa-pyxd__gallery-slide');
      firstSlide.appendChild(viewport);
      this._reattachDetached();
      this._gallerySlot = firstSlide;

      Array.from(firstSlide.children).forEach((child) => {
        if (child !== viewport) detach(child);
      });

      // The matching thumbnail still shows the photo we just replaced, so
      // tapping it previews something the slide no longer contains. Label it
      // as the live view instead. (The canvas is CORS-tainted by Pyxd's
      // textures, so it cannot be snapshotted into an <img>.)
      const firstThumb = gallery.querySelector(
        '.casa-product-gallery__thumbnail-carousel .splide__slide'
      );
      if (firstThumb && !firstThumb.querySelector('.casa-pyxd__thumb-badge')) {
        firstThumb.classList.add('casa-pyxd__thumb');
        const badge = document.createElement('span');
        badge.className = 'casa-pyxd__thumb-badge';
        badge.textContent = 'Your fabric';
        firstThumb.appendChild(badge);
      }
    } else if (desktop) {
      if (viewport.closest('.casa-pyxd__gallery-slot')) return;
      const slot = document.createElement('div');
      slot.className = 'casa-pyxd__gallery-slot';
      desktop.insertBefore(slot, desktop.firstElementChild);
      // Move the viewport first, THEN undo the previous mount, so the old
      // slot/slide no longer holds it and can be torn down cleanly.
      slot.appendChild(viewport);
      this._reattachDetached();
      this._gallerySlot = slot;
      // Resolve the image to replace only after the restore, so we detach
      // the image that is actually first at this point.
      detach(desktop.querySelector('.casa-product-gallery__desktop-image'));
    } else {
      return;
    }

    this.classList.add('casa-pyxd--gallery-mounted');
  }

  /* ---------- Pyxd bootstrap ---------- */

  /** `pyxdDrapingReady` is a one-shot event: if the SDK finished loading
   *  before this element upgraded, listening alone waits forever. Poll for
   *  window.pyxdDraping as well and take whichever resolves first. */
  _waitForPyxd(timeoutMs = 15000) {
    return new Promise((resolve, reject) => {
      if (window.pyxdDraping) return resolve(window.pyxdDraping);

      let settled = false;
      const finish = (value) => {
        if (settled) return;
        settled = true;
        clearInterval(poll);
        clearTimeout(timer);
        document.removeEventListener('pyxdDrapingReady', onReady);
        resolve(value);
      };

      const onReady = () => finish(window.pyxdDraping);
      document.addEventListener('pyxdDrapingReady', onReady);

      const poll = setInterval(() => {
        if (window.pyxdDraping) finish(window.pyxdDraping);
      }, 150);

      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        clearInterval(poll);
        document.removeEventListener('pyxdDrapingReady', onReady);
        reject(new Error('Pyxd draping SDK did not load'));
      }, timeoutMs);
    });
  }

  async _waitForCanvasSize(timeoutMs = 2000) {
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      if (this.canvas.clientWidth > 0 && this.canvas.clientHeight > 0) return;
      await new Promise((r) => setTimeout(r, 50));
    }
  }

  async _waitForReady(pyxd, timeoutMs = 15000) {
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      let surfaces = [];
      try { surfaces = pyxd.getSurfaceList() || []; } catch (e) { /* keep polling */ }
      if (surfaces.length > 0) {
        let swatches = [];
        try { swatches = pyxd.getSwatchList(surfaces[0]) || []; } catch (e) { /* keep polling */ }
        if (swatches.length > 0) return { surfaceId: surfaces[0], swatches };
      }
      await new Promise((r) => setTimeout(r, 150));
    }
    return null;
  }

  /* ---------- Panel ---------- */

  _bindPanel() {
    if (this.comToggle) {
      this.comToggle.addEventListener('click', () => this._setCom(!this.comSelected));
    }

    if (this.additionalEl) {
      this.additionalEl.addEventListener('change', () => this._autoSave());
    }

    const addBtn = this.querySelector('[data-pyxd-add]');
    if (addBtn) addBtn.addEventListener('click', () => this._addToCart());

    this.querySelectorAll('[data-pyxd-leg]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.querySelectorAll('[data-pyxd-leg]').forEach((b) => {
          b.setAttribute('aria-checked', b === btn ? 'true' : 'false');
        });
        this._syncSelectionLabels();
        this._autoSave();
      });
    });

    this.querySelectorAll('[data-pyxd-castor]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.querySelectorAll('[data-pyxd-castor]').forEach((b) => {
          b.setAttribute('aria-checked', b === btn ? 'true' : 'false');
        });
        this._syncSelectionLabels();
        this._autoSave();
      });
    });

    const qty = this.querySelector('[data-pyxd-qty]');
    const down = this.querySelector('[data-pyxd-qty-down]');
    const up = this.querySelector('[data-pyxd-qty-up]');
    if (qty && down && up) {
      down.addEventListener('click', () => {
        qty.value = Math.max(1, (parseInt(qty.value, 10) || 1) - 1);
        this._autoSave();
      });
      up.addEventListener('click', () => {
        qty.value = (parseInt(qty.value, 10) || 1) + 1;
        this._autoSave();
      });
      qty.addEventListener('change', () => {
        qty.value = Math.max(1, parseInt(qty.value, 10) || 1);
        this._autoSave();
      });
    }
  }

  /** Design note: featured fabrics at the top, a different count on desktop
   *  vs mobile. We render the desktop set and flag the overflow so CSS can
   *  hide it below 750px — one DOM, no resize listener. */
  _renderFeatured() {
    if (!this.featuredEl) return;
    this.featuredEl.innerHTML = '';
    const current = this._currentSwatchId();

    // The configured counts are TOTAL option cells, and the COM tile takes
    // one of them — so the swatch list is one shorter than the count.
    const desktopSwatches = Math.max(0, this.featuredDesktop - 1);
    const mobileSwatches = Math.max(0, this.featuredMobile - 1);

    this.swatches.slice(0, desktopSwatches).forEach((swatch, i) => {
      const btn = this._buildSwatchButton(swatch, current);
      if (i >= mobileSwatches) btn.dataset.beyondMobile = '';
      this.featuredEl.appendChild(btn);
    });
  }

  _buildSwatchButton(swatch, currentId) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'casa-pyxd__swatch';
    btn.dataset.swatchId = swatch.swatchId;
    btn.title = swatch.label;
    btn.setAttribute('aria-label', swatch.label);
    btn.setAttribute('aria-pressed', currentId === swatch.swatchId ? 'true' : 'false');
    const img = document.createElement('img');
    img.src = swatch.thumbUrl;
    img.alt = '';
    img.loading = 'lazy';
    btn.appendChild(img);
    btn.addEventListener('click', this._onSwatchClick);
    return btn;
  }

  _currentSwatchId() {
    if (!this.pyxd || !this.surfaceId) return null;
    try {
      const config = this.pyxd.getConfig();
      return (config && config.surfaces && config.surfaces[this.surfaceId]) || null;
    } catch (e) {
      return null;
    }
  }

  async _onSwatchClick(event) {
    await this._selectSwatch(event.currentTarget.dataset.swatchId);
  }

  /** Design note: "The sofa color should change on [select]" — drape
   *  immediately, no confirm step. */
  async _selectSwatch(swatchId) {
    if (!swatchId || !this.pyxd || !this.surfaceId) return;
    try {
      await this.pyxd.setMaterial(this.surfaceId, swatchId);
    } catch (err) {
      console.error('[casa-pyxd-canvas] setMaterial rejected', err);
      return;
    }
    // Picking a real fabric clears COM.
    if (this.comSelected) this._setCom(false);
    this._syncPressedState(swatchId);
    this._syncSelectionLabels();
    this._syncUrl(swatchId);
    this._autoSave();
  }

  /** Keep the chosen fabric in the URL so the configuration survives a
   *  reload and can be shared. Uses replaceState so it doesn't add a
   *  history entry per swatch click. */
  _syncUrl(swatchId) {
    try {
      const url = new URL(window.location.href);
      if (this.comSelected) {
        url.searchParams.set('fabric', 'com');
      } else if (swatchId) {
        url.searchParams.set('fabric', swatchId);
      } else {
        url.searchParams.delete('fabric');
      }
      window.history.replaceState({}, '', url);
    } catch (e) {
      /* URL API unavailable — persistence is a nicety, never block on it */
    }
  }

  /** A `fabric` param wins over the saved localStorage selection, so a
   *  shared link always shows what the sender configured. */
  _swatchFromUrl() {
    try {
      return new URL(window.location.href).searchParams.get('fabric');
    } catch (e) {
      return null;
    }
  }

  _syncPressedState(swatchId) {
    this.querySelectorAll('[data-swatch-id]').forEach((b) => {
      b.setAttribute('aria-pressed', b.dataset.swatchId === swatchId ? 'true' : 'false');
    });
  }

  _setCom(on) {
    this.comSelected = !!on;
    if (this.comToggle) this.comToggle.setAttribute('aria-pressed', String(this.comSelected));
    if (this.comNote) this.comNote.hidden = !this.comSelected;
    if (this.comSelected) {
      this.querySelectorAll('[data-swatch-id]').forEach((b) => b.setAttribute('aria-pressed', 'false'));
      // Reset the render: with COM the piece is made in the customer's own
      // material, so leaving the last-chosen fabric draped misrepresents it.
      this._resetDrapeToDefault();
    }
    this._syncSelectionLabels();
    // Keep the URL in step: COM is a shareable state too.
    if (this.comSelected) this._syncUrl(null);
    this._autoSave();
  }

  /** Return the 3D model to the frame's starting fabric. */
  async _resetDrapeToDefault() {
    if (!this.pyxd || !this.surfaceId || !this._defaultSwatchId) return;
    if (this._currentSwatchId() === this._defaultSwatchId) return;
    try {
      await this.pyxd.setMaterial(this.surfaceId, this._defaultSwatchId);
    } catch (err) {
      console.error('[casa-pyxd-canvas] could not reset drape for COM', err);
      return;
    }
    this._syncSelectionLabels();
    this._refreshPricing();
  }

  _syncSelectionLabels() {
    if (this.fabricNameEl) {
      if (this.comSelected) {
        this.fabricNameEl.textContent = 'Customer Own Material';
      } else {
        // The design labels this with the fabric NAME only ("fabric: Giardino
        // Foglia"); Pyxd labels are "<Name> - <Colorway>". The colorway is
        // still shown on the drawer card and in the cart line.
        const id = this._currentSwatchId();
        const match = (this.swatches || []).find((s) => s.swatchId === id);
        this.fabricNameEl.textContent = match ? String(match.label).split(' - ')[0] : '—';
      }
    }

    const leg = this.querySelector('[data-pyxd-leg][aria-checked="true"]');
    const legName = this.querySelector('[data-pyxd-legs-name]');
    if (legName) legName.textContent = leg ? leg.dataset.pyxdLeg : '—';

    const castor = this.querySelector('[data-pyxd-castor][aria-checked="true"]');
    const castorName = this.querySelector('[data-pyxd-castors-name]');
    if (castorName) castorName.textContent = castor ? castor.dataset.pyxdCastor : '—';
  }

  /* ---------- Drawer ---------- */

  _bindDrawer() {
    const open = this.querySelector('[data-pyxd-open-drawer]');
    if (open) open.addEventListener('click', () => this._openDrawer());

    this.querySelectorAll('[data-pyxd-drawer-close]').forEach((el) => {
      el.addEventListener('click', () => this._closeDrawer());
    });

    const clear = this.querySelector('[data-pyxd-clear-all]');
    if (clear) {
      clear.addEventListener('click', () => {
        Object.keys(this.filters).forEach((k) => this.filters[k].clear());
        this._renderFilterMenus();
        this._renderMobileFilters();
        this._renderResults();
      });
    }

    this.querySelectorAll('[data-pyxd-filter]').forEach((group) => {
      const toggle = group.querySelector('.casa-pyxd-filter__toggle');
      toggle.addEventListener('click', (e) => {
        e.stopPropagation();
        const isOpen = toggle.getAttribute('aria-expanded') === 'true';
        this._closeAllFilterMenus();
        if (!isOpen) {
          toggle.setAttribute('aria-expanded', 'true');
          group.querySelector('[data-pyxd-filter-menu]').hidden = false;
        }
      });
    });

    const mToggle = this.querySelector('[data-pyxd-mfilters-toggle]');
    const mPanel = this.querySelector('[data-pyxd-mfilters-panel]');
    const mBox = this.querySelector('[data-pyxd-mfilters-box]');
    if (mToggle && mPanel) {
      mToggle.addEventListener('click', () => {
        const open = mToggle.getAttribute('aria-expanded') === 'true';
        mToggle.setAttribute('aria-expanded', String(!open));
        mPanel.hidden = open;
        // Expanded, the bar and panel read as one cream box; the product
        // count gives way to the applied-filter pills.
        if (mBox) mBox.classList.toggle('casa-pyxd-mfilters__box--open', !open);
      });
    }

    if (this.drawer) this.drawer.addEventListener('click', this._onDrawerClick);

    document.addEventListener('click', this._onDocumentClick);
    document.addEventListener('keydown', this._onKeydown);
  }

  _isMobile() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  /** On mobile the drawer covers the viewport, so the shopper would lose
   *  sight of the piece while choosing. Move the live canvas into the
   *  drawer's preview slot (the same element, so it stays interactive) and
   *  put it back on close. */
  _moveViewportToDrawer() {
    // NB: the viewport may already have been relocated into the product
    // gallery, which is OUTSIDE this component — so scope the lookup to the
    // document and keep a direct reference rather than re-querying `this`.
    const viewport = this.viewportEl || document.querySelector('.casa-pyxd__viewport-col');
    if (!this.previewSlot || !this._isMobile() || !viewport) return;
    if (viewport.parentElement === this.previewSlot) return;
    this.viewportEl = viewport;
    this._viewportHome = viewport.parentElement;
    this.previewSlot.appendChild(viewport);
  }

  _restoreViewport() {
    const viewport = this.viewportEl || document.querySelector('.casa-pyxd__viewport-col');
    if (!viewport) return;

    // The breakpoint changed while the drawer was open, so the old home
    // belongs to the now-hidden tree — re-mount instead of returning to it.
    if (this._remountPending) {
      this._remountPending = false;
      this._mountViewportInGallery();
      return;
    }

    if (!this._viewportHome) return;
    if (viewport.parentElement === this._viewportHome) return;

    // The home is the gallery slot/slide it was mounted into, which differs
    // between breakpoints. `isConnected` alone is not enough: the hidden
    // tree's nodes are still connected, so a stale home from the other
    // breakpoint would pass that test and put the canvas somewhere invisible.
    // Require the home to belong to the tree that is live right now.
    const home = this._viewportHome;
    const homeTree = this._isMobile()
      ? '.casa-product-gallery__mobile'
      : '.casa-product-gallery__desktop';
    const homeIsCurrent =
      home.isConnected && (home.closest(homeTree) || home.matches(homeTree));

    if (homeIsCurrent) home.appendChild(viewport);
    else this._mountViewportInGallery();
  }

  _openDrawer() {
    if (!this.drawer) return;
    // Re-entrancy guard: opening twice would overwrite the saved scroll
    // offset with the already-locked value, dumping the shopper at the top
    // of the page on close.
    if (this.drawer.open) return;
    this._renderResults();
    this._renderMobileFilters();
    this._moveViewportToDrawer();
    // showModal() gives us the native backdrop + focus trap; the slide-in
    // comes from the theme's .dialog-drawer[open] animation.
    if (typeof this.drawer.showModal === 'function') this.drawer.showModal();
    else this.drawer.setAttribute('open', '');

    // Lock the page behind the drawer. A native modal dialog blocks
    // interaction but does NOT stop the document scrolling underneath,
    // which on mobile reads as two competing scroll areas.
    this._scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this._scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.overflow = 'hidden';
  }

  _unlockPageScroll() {
    if (document.body.style.position !== 'fixed') return;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.overflow = '';
    if (typeof this._scrollY !== 'number') return;

    // The theme sets `html { scroll-behavior: smooth }`, so a plain
    // scrollTo animates from the top back down to the saved offset — the
    // page visibly jumps up and then scrolls down. Restore instantly, and
    // suppress smooth scrolling for the duration in case the browser
    // ignores the behavior option.
    const root = document.documentElement;
    const previous = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';
    window.scrollTo({ top: this._scrollY, left: 0, behavior: 'instant' });
    // Restore on the next frame, once the jump has been committed.
    requestAnimationFrame(() => {
      root.style.scrollBehavior = previous;
    });
  }

  /** Mirrors the theme's drawer close: add .dialog-closing so the slide-out
   *  animation runs, then actually close once it finishes. */
  _closeDrawer() {
    if (!this.drawer || !this.drawer.open) return;
    this._closeAllFilterMenus();

    const finish = () => {
      this.drawer.classList.remove('dialog-closing');
      this.drawer.close();
      this._restoreViewport();
    };

    // Release the page BEFORE the slide-out runs. If the body stays
    // position:fixed for the length of the animation the document sits at
    // scroll 0 the whole time, so the page visibly jumps to the top and
    // then back down once the offset is restored.
    this._unlockPageScroll();

    this.drawer.classList.add('dialog-closing');

    // Fall back to a timer if the animation never fires (reduced motion,
    // or the class not matching) so the drawer can't get stuck open.
    let done = false;
    const once = () => {
      if (done) return;
      done = true;
      this.drawer.removeEventListener('animationend', once);
      clearTimeout(timer);
      finish();
    };
    this.drawer.addEventListener('animationend', once);
    const timer = setTimeout(once, 500);
  }

  _closeAllFilterMenus() {
    this.querySelectorAll('[data-pyxd-filter]').forEach((g) => {
      g.querySelector('.casa-pyxd-filter__toggle').setAttribute('aria-expanded', 'false');
      g.querySelector('[data-pyxd-filter-menu]').hidden = true;
    });
  }

  /** Design note: "When clicking out of the filters area, it should
   *  minimize automatically." */
  _onDocumentClick(event) {
    if (!this.drawer || !this.drawer.open) return;
    const insideFilters = event.target.closest('[data-pyxd-filters]');
    if (!insideFilters) this._closeAllFilterMenus();
  }

  /** A native modal <dialog> closes itself on Escape, which would skip the
   *  slide-out. Intercept: first Escape minimises an open filter menu,
   *  otherwise run our animated close. */
  _onKeydown(event) {
    if (event.key !== 'Escape' || !this.drawer || !this.drawer.open) return;
    event.preventDefault();
    const anyMenuOpen = Array.from(this.querySelectorAll('[data-pyxd-filter-menu]'))
      .some((m) => !m.hidden);
    if (anyMenuOpen) this._closeAllFilterMenus();
    else this._closeDrawer();
  }

  /** Clicking the ::backdrop targets the dialog element itself. */
  _onDrawerClick(event) {
    if (event.target === this.drawer) this._closeDrawer();
  }

  _renderFilterMenus() {
    Object.keys(FACET_GROUPS).forEach((key) => {
      const group = this.querySelector(`[data-pyxd-filter="${key}"]`);
      if (!group) return;
      const menu = group.querySelector('[data-pyxd-filter-menu]');
      menu.innerHTML = '';

      // Only offer facets that actually match at least one swatch.
      const available = FACET_GROUPS[key].filter((facet) =>
        (this.swatches || []).some((s) => s.facets[key].includes(facet.label))
      );

      // Only Colour has real swatches. The other groups would otherwise
      // render an empty grey box per option, so they list as plain text in a
      // single column until artwork exists for them.
      const groupHasSwatches = available.some((facet) => !!facet.swatch);
      menu.classList.toggle('casa-pyxd-filter__menu--list', !groupHasSwatches);

      available.forEach((facet) => {
        const selected = this.filters[key].has(facet.label);

        // Design note: filters auto-apply on select — no Apply button.
        const toggle = () => {
          if (this.filters[key].has(facet.label)) this.filters[key].delete(facet.label);
          else this.filters[key].add(facet.label);
          this._renderResults();
          this._syncFilterChips();
        };

        let option;
        if (facet.swatch) {
          // Colour: the swatch itself is the control, and selection reads as
          // a dark outline around it.
          option = document.createElement('button');
          option.type = 'button';
          option.className = 'casa-pyxd-filter__option';
          option.setAttribute('aria-pressed', String(selected));

          const dot = document.createElement('span');
          dot.className = 'casa-pyxd-filter__dot';
          dot.style.background = facet.swatch;
          option.appendChild(dot);

          const text = document.createElement('span');
          text.className = 'casa-pyxd-filter__option-label';
          text.textContent = facet.label;
          option.appendChild(text);

          option.addEventListener('click', () => {
            toggle();
            option.setAttribute('aria-pressed', String(this.filters[key].has(facet.label)));
          });
        } else {
          // Groups without artwork use a checkbox, matching the selected
          // state on the store's own PLP filters.
          option = document.createElement('label');
          option.className = 'casa-pyxd-filter__option casa-pyxd-filter__option--check';

          const input = document.createElement('input');
          input.type = 'checkbox';
          input.checked = selected;
          input.addEventListener('change', toggle);
          option.appendChild(input);

          const text = document.createElement('span');
          text.className = 'casa-pyxd-filter__option-label';
          text.textContent = facet.label;
          option.appendChild(text);
        }

        menu.appendChild(option);
      });

      if (!available.length) {
        const empty = document.createElement('p');
        empty.className = 'casa-pyxd-drawer__empty';
        empty.textContent = 'No options';
        menu.appendChild(empty);
      }
    });

    this._syncFilterChips();
  }

  _syncFilterChips() {
    Object.keys(FACET_GROUPS).forEach((key) => {
      const group = this.querySelector(`[data-pyxd-filter="${key}"]`);
      if (!group) return;
      const chip = group.querySelector('[data-pyxd-filter-chip]');
      const selected = Array.from(this.filters[key]);
      if (!chip) return;
      if (!selected.length) {
        chip.hidden = true;
        return;
      }
      chip.hidden = false;
      const facet = FACET_GROUPS[key].find((f) => f.label === selected[0]);
      const groupHasSwatches = FACET_GROUPS[key].some((f) => !!f.swatch);

      if (groupHasSwatches) {
        chip.classList.remove('casa-pyxd-filter__chip--text');
        chip.style.setProperty('--chip-fill', (facet && facet.swatch) || '#d8d2c8');
        chip.textContent = selected.length > 1 ? String(selected.length) : '';
      } else {
        // No artwork for this group, so name the selection rather than
        // showing an empty colour block ("Type  Mohair").
        chip.classList.add('casa-pyxd-filter__chip--text');
        chip.style.removeProperty('--chip-fill');
        chip.textContent =
          selected.length > 1 ? `${selected.length} selected` : selected[0];
      }
    });

    this._renderAppliedTags();
  }

  /** Mobile filters: one inline cream panel with grouped, counted sections.
   *  Color renders as a swatch grid; the other facets as checkbox lists,
   *  matching the mobile design. */
  _renderMobileFilters() {
    if (!this.mGroupsEl) return;
    this.mGroupsEl.innerHTML = '';

    const LABELS = { color: 'Colors', design: 'Design', fabric: 'Type' };

    Object.keys(FACET_GROUPS).forEach((key) => {
      const available = FACET_GROUPS[key].filter((facet) =>
        (this.swatches || []).some((s) => s.facets[key].includes(facet.label))
      );
      if (!available.length) return;

      const group = document.createElement('div');
      group.className = 'casa-pyxd-mgroup';

      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'casa-pyxd-mgroup__toggle';
      toggle.setAttribute('aria-expanded', 'true');
      toggle.innerHTML =
        `<span>${LABELS[key]} (${available.length})</span>` +
        '<svg class="casa-pyxd-filter__caret" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
        '<path fill-rule="evenodd" clip-rule="evenodd" d="M5.64453 9.35356L6.35164 8.64645L11.9981 14.2929L17.6445 8.64645L18.3516 9.35356L11.9981 15.7071L5.64453 9.35356Z" fill="currentColor"/></svg>';
      group.appendChild(toggle);

      const body = document.createElement('div');
      body.className = 'casa-pyxd-mgroup__body';

      const onToggle = (facet, el) => {
        if (this.filters[key].has(facet.label)) this.filters[key].delete(facet.label);
        else this.filters[key].add(facet.label);
        const on = this.filters[key].has(facet.label);
        if (el.tagName === 'BUTTON') el.setAttribute('aria-pressed', String(on));
        else el.checked = on;
        this._renderResults();
        this._syncFilterChips();
      };

      if (key === 'color') {
        const grid = document.createElement('div');
        grid.className = 'casa-pyxd-mgroup__swatches';
        available.forEach((facet) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'casa-pyxd-mgroup__swatch';
          b.title = facet.label;
          b.setAttribute('aria-label', facet.label);
          b.setAttribute('aria-pressed', String(this.filters[key].has(facet.label)));
          if (facet.swatch) b.style.setProperty('--swatch-fill', facet.swatch);
          b.addEventListener('click', () => onToggle(facet, b));
          grid.appendChild(b);
        });
        body.appendChild(grid);
      } else {
        available.forEach((facet) => {
          const label = document.createElement('label');
          label.className = 'casa-pyxd-mgroup__check';
          const input = document.createElement('input');
          input.type = 'checkbox';
          input.checked = this.filters[key].has(facet.label);
          input.addEventListener('change', () => onToggle(facet, input));
          label.appendChild(input);
          const span = document.createElement('span');
          span.textContent = facet.label;
          label.appendChild(span);
          body.appendChild(label);
        });
      }

      toggle.addEventListener('click', () => {
        const open = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', String(!open));
        body.hidden = open;
      });

      group.appendChild(body);
      this.mGroupsEl.appendChild(group);
    });
  }

  _renderAppliedTags() {
    if (!this.appliedEl) return;
    const tags = [];
    Object.keys(this.filters).forEach((key) => {
      this.filters[key].forEach((value) => tags.push({ key, value }));
    });

    const fill = (container) => {
      if (!container) return;
      container.innerHTML = '';
      container.hidden = tags.length === 0;
      // The design labels pills with the group's display name: "Type: Velvet".
      const GROUP_LABELS = { color: 'Color', design: 'Design', fabric: 'Type' };
      tags.forEach(({ key, value }) => {
        const tag = document.createElement('span');
        tag.className = 'casa-pyxd-drawer__tag';
        tag.textContent = `${GROUP_LABELS[key] || key}: ${value}`;
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.setAttribute('aria-label', `Remove ${value} filter`);
        remove.textContent = '×';
        remove.addEventListener('click', () => {
          this.filters[key].delete(value);
          this._renderFilterMenus();
          this._renderMobileFilters();
          this._renderResults();
        });
        tag.appendChild(remove);
        container.appendChild(tag);
      });
    };

    fill(this.appliedEl);
    fill(this.mAppliedEl);
  }

  _filteredSwatches() {
    return (this.swatches || []).filter((s) =>
      Object.keys(this.filters).every((key) => {
        const active = this.filters[key];
        if (!active.size) return true;
        return Array.from(active).some((v) => s.facets[key].includes(v));
      })
    );
  }

  _renderResults() {
    if (!this.resultsEl) return;
    const list = this._filteredSwatches();
    const current = this._currentSwatchId();

    const countText = `${list.length} ${list.length === 1 ? 'Product' : 'Products'}`;
    if (this.countEl) this.countEl.textContent = countText;
    if (this.mCountEl) this.mCountEl.textContent = countText;

    this.resultsEl.innerHTML = '';

    if (!list.length) {
      const empty = document.createElement('p');
      empty.className = 'casa-pyxd-drawer__empty';
      empty.textContent = 'No fabrics match these filters.';
      this.resultsEl.appendChild(empty);
      this._syncFilterChips();
      return;
    }

    ['Plains', 'Patterns'].forEach((groupName) => {
      const items = list.filter((s) => s.group === groupName);
      if (!items.length) return;

      const section = document.createElement('div');
      section.className = 'casa-pyxd-drawer__group';

      const title = document.createElement('h3');
      title.className = 'casa-pyxd-drawer__group-title';
      title.textContent = groupName;
      section.appendChild(title);

      const grid = document.createElement('div');
      grid.className = 'casa-pyxd-drawer__grid';

      items.forEach((swatch) => {
        const card = document.createElement('button');
        card.type = 'button';
        card.className = 'casa-pyxd-drawer__card';
        card.dataset.swatchId = swatch.swatchId;
        card.setAttribute('aria-pressed', current === swatch.swatchId ? 'true' : 'false');

        const img = document.createElement('img');
        img.src = swatch.thumbUrl;
        img.alt = '';
        img.loading = 'lazy';
        card.appendChild(img);

        const name = document.createElement('span');
        name.className = 'casa-pyxd-drawer__card-name';
        name.textContent = swatch.label;
        card.appendChild(name);

        card.addEventListener('click', async () => {
          await this._selectSwatch(swatch.swatchId);
          this._renderFeatured();
          this._renderResults();
        });

        grid.appendChild(card);
      });

      section.appendChild(grid);
      this.resultsEl.appendChild(section);
    });

    this._syncFilterChips();
  }

  /* ---------- Persistence ---------- */

  _setStatus(text) {
    if (this.statusEl) this.statusEl.textContent = text || '';
  }

  _readSaved() {
    try {
      const raw = localStorage.getItem(this.storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  _writeSaved(payload) {
    try { localStorage.setItem(this.storageKey, JSON.stringify(payload)); } catch (e) { /* ignore */ }
  }

  _autoSave() {
    if (!this.pyxd) return;
    let sku = '';
    try { sku = this.pyxd.getSku() || ''; } catch (e) { sku = ''; }
    let configuration = null;
    try { configuration = this.pyxd.getConfig(); } catch (e) { /* leave null */ }

    const leg = this.querySelector('[data-pyxd-leg][aria-checked="true"]');
    const castor = this.querySelector('[data-pyxd-castor][aria-checked="true"]');
    const qty = this.querySelector('[data-pyxd-qty]');
    const fabricId = this._currentSwatchId();
    const fabric = (this.swatches || []).find((s) => s.swatchId === fabricId);

    this._writeSaved({
      sku,
      configuration,
      productTitle: this.productTitle,
      productHandle: this.productHandle,
      frameId: this.frameId,
      fabric: this.comSelected ? 'Customer Own Material' : (fabric ? fabric.label : ''),
      com: this.comSelected,
      legs: leg ? leg.dataset.pyxdLeg : '',
      castors: castor ? castor.dataset.pyxdCastor : '',
      additionalCustomization: !!(this.additionalEl && this.additionalEl.checked),
      quantity: qty ? parseInt(qty.value, 10) || 1 : 1,
      savedAt: Date.now(),
    });

    this._refreshPricing();
  }

  /* ---------- Fabric resolution, pricing and cart ---------- */

  /** Explicit `swatchId:product-handle` lines from theme settings. This is
   *  the authoritative mapping — Pyxd swatches carry no product ID, so
   *  without it we fall back to matching on the label (see _resolveFabric),
   *  which is brittle if either catalogue is renamed. */
  _parseSwatchMap(raw) {
    const map = new Map();
    if (!raw) return map;
    raw.split('\n').forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith('#')) return;
      const idx = trimmed.indexOf(':');
      if (idx === -1) return;
      const id = trimmed.slice(0, idx).trim();
      const handle = trimmed.slice(idx + 1).trim();
      if (id && handle) map.set(id, handle);
    });
    return map;
  }

  static _normalize(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]/g, '');
  }

  /** Resolve a Pyxd swatch to a Shopify fabric variant.
   *
   *  Pyxd labels read "<Fabric Name> - <Colorway>" and Casa's fabric
   *  products carry a Color option plus an Order Stage option, so we pick
   *  the matching colorway at the "Order" stage (Memo/CFA are samples).
   *  Returns null when nothing matches — the caller surfaces that rather
   *  than adding a wrong line to the cart. */
  async _resolveFabric(swatch) {
    if (!swatch) return null;
    if (this.fabricCache.has(swatch.swatchId)) return this.fabricCache.get(swatch.swatchId);

    const N = CasaPyxdCanvas._normalize;
    const [namePart, colorPart] = String(swatch.label).split(' - ');

    const tryFetch = async (h) => {
      try {
        const res = await fetch(`/products/${h}.js`);
        return res.ok ? await res.json() : null;
      } catch (e) {
        return null;
      }
    };

    const orderPool = (p) => {
      const order = p.variants.filter((v) => /order/i.test(v.title));
      return order.length ? order : p.variants;
    };
    const findColor = (p) =>
      colorPart ? orderPool(p).find((v) => N(v.title).includes(N(colorPart))) : orderPool(p)[0];

    // Build the candidate list. An explicit mapping wins outright; otherwise
    // gather every product whose title matches the fabric name, because
    // titles are NOT unique — Casa has two products called "Bambù" (a
    // wallpaper and a fabric) with completely different colorways.
    const mapped = this.swatchMap.get(swatch.swatchId);
    let candidates = [];

    if (mapped) {
      const p = await tryFetch(mapped);
      if (p) candidates.push(p);
    } else {
      try {
        const res = await fetch(
          `/search/suggest.json?q=${encodeURIComponent(namePart)}&resources[type]=product&resources[limit]=10`
        );
        if (res.ok) {
          const json = await res.json();
          const hits = (json.resources && json.resources.results && json.resources.results.products) || [];
          const exact = hits.filter((p) => N(p.title) === N(namePart));
          const fetched = await Promise.all(exact.map((p) => tryFetch(p.handle)));
          candidates = fetched.filter(Boolean);
        }
      } catch (e) {
        /* fall through */
      }
      // Last resort: the literal handle guess.
      if (!candidates.length) {
        const p = await tryFetch(N(namePart));
        if (p) candidates.push(p);
      }
    }

    // Prefer the candidate that actually carries the swatch's colorway.
    // Never silently fall back to a different colour — a wrong fabric in
    // the cart is worse than telling the customer we couldn't match it.
    let product = null;
    let variant = null;
    for (const candidate of candidates) {
      const hit = findColor(candidate);
      if (hit) {
        product = candidate;
        variant = hit;
        break;
      }
    }

    if (!product || !variant) {
      if (candidates.length) {
        console.warn(
          `[casa-pyxd-canvas] no "${colorPart}" colorway on ${candidates
            .map((c) => c.handle)
            .join(', ')} for swatch "${swatch.label}"`
        );
      }
      this.fabricCache.set(swatch.swatchId, null);
      return null;
    }

    const resolved = {
      productTitle: product.title,
      productHandle: product.handle,
      variantId: variant.id,
      variantTitle: variant.title,
      pricePerYard: variant.price,
      available: variant.available,
    };

    this.fabricCache.set(swatch.swatchId, resolved);
    return resolved;
  }

  /** Derive the market's money pattern once from the sample Liquid rendered
   *  for us, so client-side totals format exactly like every server-rendered
   *  price on the page instead of hardcoding US dollars (CASA-462). */
  _moneyPattern() {
    if (this._pattern) return this._pattern;

    const sample = this.dataset.moneySample || '$1,234,567.89';
    const m = sample.match(/^(\D*?)(\d[\d.,'\s\u00a0]*\d)(\D*)$/);
    if (!m) {
      return (this._pattern = { prefix: '$', suffix: '', group: ',', decimal: '.' });
    }

    const [, prefix, digits, suffix] = m;
    const seps = digits.replace(/\d/g, '');
    const lastSep = seps.slice(-1);
    // Two digits after the final separator means it's the decimal mark; a
    // zero-decimal currency (JPY) ends on a group separator instead.
    const tail = lastSep ? digits.slice(digits.lastIndexOf(lastSep) + 1) : '';
    const hasDecimals = Boolean(lastSep) && tail.length === 2;

    return (this._pattern = {
      prefix,
      suffix,
      decimal: hasDecimals ? lastSep : '',
      group: hasDecimals ? (seps.length > 1 ? seps[0] : '') : lastSep,
    });
  }

  _formatMoney(cents) {
    const p = this._moneyPattern();
    const negative = cents < 0;
    const abs = Math.abs(Math.round(cents));

    let whole = String(Math.floor(abs / 100));
    if (p.group) whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, p.group);

    let out = p.prefix + whole;
    if (p.decimal) out += p.decimal + String(abs % 100).padStart(2, '0');
    out += p.suffix;
    if (negative) out = '-' + out;

    // Some markets' money format already carries the code (CHF, for one), so
    // appending it again would read "CHF 3'750.00 CHF".
    const code = this.dataset.currencyCode;
    if (this.dataset.showCurrencyCode === 'true' && code && !out.includes(code)) {
      out += ' ' + code;
    }
    return out;
  }

  /** Total = piece price + (fabric per-yard x yardage required).
   *  Pyxd supplies no pricing, so everything comes from Shopify. */
  async _refreshPricing() {
    const label = this.querySelector('[data-pyxd-submit-label]');
    const yardEl = this.querySelector('[data-pyxd-yardage-text]');
    if (!label) return;

    if (this.comSelected) {
      // Customer supplies the material: no fabric line, no upcharge.
      if (this.yardageEl) this.yardageEl.hidden = true;
      label.textContent = `Add to cart ${this._formatMoney(this.basePrice)}`;
      return;
    }

    const swatch = this._currentSwatch();
    const fabric = await this._resolveFabric(swatch);
    this.currentFabric = fabric;

    if (!fabric) {
      if (this.yardageEl) this.yardageEl.hidden = true;
      label.textContent = `Add to cart ${this._formatMoney(this.basePrice)}`;
      // A selected swatch we can't price is a data gap, not a valid state —
      // say so rather than quoting the piece alone.
      this._setError(
        swatch
          ? 'We could not match this fabric to a product yet. Contact us to order it.'
          : ''
      );
      return;
    }
    this._setError('');

    const fabricTotal = fabric.pricePerYard * this.yardage;
    if (this.yardageEl && yardEl) {
      this.yardageEl.hidden = false;
      yardEl.textContent =
        `${this.yardage} yrds of ${fabric.productTitle} will be added to cart ` +
        `(${this._formatMoney(fabricTotal)})`;
    }
    label.textContent = `Add to cart ${this._formatMoney(this.basePrice + fabricTotal)}`;
  }

  _currentSwatch() {
    const id = this._currentSwatchId();
    return (this.swatches || []).find((s) => s.swatchId === id) || null;
  }

  _setError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message || '';
    this.errorEl.hidden = !message;
  }

  /** Adds the piece plus its upholstery as two cart lines. The fabric line
   *  carries the Pyxd selection as line-item properties so the workroom can
   *  see exactly what was configured. */
  async _addToCart() {
    if (!this.variantId) return;
    const btn = this.querySelector('[data-pyxd-add]');
    const qtyEl = this.querySelector('[data-pyxd-qty]');
    const quantity = qtyEl ? Math.max(1, parseInt(qtyEl.value, 10) || 1) : 1;

    const leg = this.querySelector('[data-pyxd-leg][aria-checked="true"]');
    const castor = this.querySelector('[data-pyxd-castor][aria-checked="true"]');
    const swatch = this._currentSwatch();
    const fabric = this.comSelected ? null : await this._resolveFabric(swatch);

    if (!this.comSelected && swatch && !fabric) {
      this._setError(
        'We could not match that fabric to a product. Please choose another fabric or contact us.'
      );
      return;
    }

    // The piece's only Shopify variant is literally titled "COM (Customer's
    // Own Material)", which contradicts the line when a Casa fabric was
    // chosen. State the actual selection as a property so the cart reads
    // "<Fabric> Fabric <N> yrds" per the design, rather than showing COM.
    const properties = {};
    if (this.comSelected) {
      properties.Fabric = "Customer's Own Material";
    } else if (fabric) {
      properties.Fabric = `${fabric.productTitle} Fabric ${this.yardage} yrds`;
    } else if (swatch) {
      properties.Fabric = swatch.label;
    }

    if (leg) properties.Legs = leg.dataset.pyxdLeg;
    if (castor) properties.Castors = castor.dataset.pyxdCastor;
    if (this.additionalEl && this.additionalEl.checked) {
      properties['Additional customization'] = 'Requested';
    }

    const parentVariantId = Number(this.variantId);
    const items = [{ id: parentVariantId, quantity, properties }];

    if (fabric) {
      // Nested cart line (add-on): `parent_id` is the PARENT'S VARIANT ID and
      // is used when both parent and child are added in the same request.
      // Shopify then renders the fabric indented under the piece in cart,
      // checkout, order emails and admin — and removes it automatically if
      // the piece is removed. See "Nested cart lines" in the Cart AJAX API.
      items.push({
        id: fabric.variantId,
        quantity: Math.ceil(this.yardage * quantity),
        parent_id: parentVariantId,
        properties: {
          'For piece': this.productTitle,
          _pyxd_swatch_id: swatch ? swatch.swatchId : '',
        },
      });
    }

    this._setError('');
    if (btn) btn.disabled = true;

    try {
      // Ask cart/add.js to return the rendered cart sections, exactly as the
      // theme's own product-form does — without this the drawer and cart
      // bubble keep showing stale markup after the add.
      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map((el) => el.dataset.sectionId)
        .filter(Boolean);

      const body = { items };
      if (sectionIds.length) body.sections = sectionIds.join(',');

      const res = await fetch(`${this._rootUrl()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const payload = await res.json().catch(() => null);

      // cart/add.js reports failures with a `status` field, not just non-2xx.
      if (!res.ok || (payload && payload.status)) {
        this._setError(
          (payload && (payload.description || payload.message)) ||
            'Could not add to cart. Please try again.'
        );
        return;
      }

      // The nested relationship is created server-side; if the store or theme
      // doesn't support it the fabric silently lands as a sibling line, which
      // is still a valid cart but reads differently. Surface it in the console
      // rather than assuming it worked.
      if (fabric && payload && Array.isArray(payload.items)) {
        const child = payload.items.find((i) => i.variant_id === fabric.variantId);
        if (child && !child.parent_relationship) {
          console.warn(
            '[casa-pyxd-canvas] fabric line was not nested under the piece — ' +
              'the store may not support nested cart lines.'
          );
        }
      }

      this._announceCartAdd(payload, quantity);
    } catch (err) {
      console.error('[casa-pyxd-canvas] add to cart failed', err);
      this._setError('Could not add to cart. Please try again.');
    } finally {
      if (btn) btn.disabled = false;
    }
  }

  _rootUrl() {
    const root = (window.Theme && window.Theme.routes && window.Theme.routes.root) || '/';
    return root.endsWith('/') ? root : `${root}/`;
  }

  /** The theme's CartAddEvent lives in an ES module (assets/events.js) and
   *  isn't reachable from a classic script, so we emit an event with the
   *  identical name and detail shape. Listeners key off `cart:update` and
   *  read `detail.data.sections` to re-render the drawer and cart bubble. */
  _announceCartAdd(payload, quantity) {
    const event = new Event('cart:update', { bubbles: true });
    event.detail = {
      resource: {},
      sourceId: String(this.variantId),
      data: {
        source: 'casa-pyxd-canvas',
        itemCount: quantity,
        variantId: String(this.variantId),
        productId: this.dataset.productId || '',
        sections: payload && payload.sections,
      },
    };
    this.dispatchEvent(event);
  }
}

if (!customElements.get('casa-pyxd-canvas')) {
  customElements.define('casa-pyxd-canvas', CasaPyxdCanvas);
}
